"use client";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useUser } from "@clerk/nextjs";
import { memo, useState } from "react";
import {
  BanknotesIcon,
  BarsArrowDownIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  FunnelIcon,
  HomeModernIcon,
  SwatchIcon,
  TagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Hidden } from "@/components/ui/hidden";
import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { SortBy } from "./sort-by";
import { Type } from "./type";
import { Modality } from "./modality";
import { Payment } from "./payment";
import { Price } from "./price";
import { Bank } from "./bank";
import { Size } from "./size";

import {
  bankCriteria,
  defaultSortingCriteria,
  defaultTypeCriteria,
  modalityCriteria,
  paymentCriteria,
  sortingCriteria,
  typeCriteria,
} from "@/constants";

import { useFilter } from "../use-filter";
import { useActions } from "../list/use-actions";
import { getUserPlanByMetadata } from "@/helper/user/client";

import type { HouseCityParam, PriceRange } from "@/types";

interface FilterContentProps {
  priceRange: PriceRange;
  onFilter: () => void;
  onClean: () => void;
  onClose?: () => void;
  hideSearchInputs?: boolean;
  variant: "sheet" | "popover";
  itemIndex?: string;
  onItemIndexChange?: (value: string) => void;
}

const FilterInfo = () => {
  const [
    {
      ordenar,
      tipo,
      modalidade,
      pagamento,
      banco,
      tamanhoArea,
      tamanhoPropriedade,
    },
  ] = useFilter();

  return (
    <>
      <FunnelIcon className="size-4" aria-hidden="true" />
      Filtrar
      <Separator orientation="vertical" />
      <div className="flex flex-col sm:flex-row items-center justify-start gap-2 flex-wrap">
        {ordenar ? (
          <Badge variant="secondary">
            Ordenação: {sortingCriteria[ordenar]}
          </Badge>
        ) : null}
        {tipo ? (
          <Badge variant="secondary">Tipo: {typeCriteria[tipo]}</Badge>
        ) : null}
        {modalidade.length > 0 ? (
          <Badge variant="secondary">
            Modalidade:{" "}
            {modalidade.map((item, index) =>
              index >= 1 && index < modalidade.length + 1
                ? `, ${modalityCriteria[item]}`
                : `${modalityCriteria[item]}`
            )}
          </Badge>
        ) : null}
        {pagamento.length > 0 ? (
          <Badge variant="secondary">
            Pagamento:{" "}
            {pagamento.map((item, index) =>
              index >= 1 && index < pagamento.length + 1
                ? `, ${paymentCriteria[item]}`
                : `${paymentCriteria[item]}`
            )}
          </Badge>
        ) : null}
        {banco.length > 0 ? (
          <Badge variant="secondary">
            Banco:{" "}
            {banco.map((item, index) =>
              index >= 1 && index < banco.length + 1
                ? `, ${bankCriteria[item]}`
                : `${bankCriteria[item]}`
            )}
          </Badge>
        ) : null}
        {tamanhoArea ? (
          <Badge variant="secondary">Área: +{tamanhoArea}m²</Badge>
        ) : null}
        {tamanhoPropriedade ? (
          <Badge variant="secondary">
            Propriedade: +{tamanhoPropriedade}m²
          </Badge>
        ) : null}
      </div>
    </>
  );
};

const FilterContent = ({
  priceRange,
  hideSearchInputs = false,
  variant,
  itemIndex,
  onItemIndexChange,
  onFilter,
  onClean,
  onClose,
}: FilterContentProps) => {
  const filterItems = [
    {
      id: "1",
      icon: BarsArrowDownIcon,
      label: "Ordenar",
      content: <SortBy hideSearchInput={hideSearchInputs} />,
    },
    {
      id: "2",
      icon: HomeModernIcon,
      label: "Tipo",
      content: <Type hideSearchInput={hideSearchInputs} />,
    },
    {
      id: "3",
      icon: TagIcon,
      label: "Modalidade",
      content: <Modality hideSearchInput={hideSearchInputs} />,
    },
    {
      id: "4",
      icon: BanknotesIcon,
      label: "Pagamento",
      content: <Payment hideSearchInput={hideSearchInputs} />,
    },
    {
      id: "5",
      icon: BuildingLibraryIcon,
      label: "Banco",
      content: <Bank hideSearchInput={hideSearchInputs} />,
    },
    {
      id: "6",
      icon: CurrencyDollarIcon,
      label: "Preço",
      content: <Price priceRange={priceRange} />,
    },
    {
      id: "7",
      icon: SwatchIcon,
      label: "Metragem",
      content: <Size />,
    },
  ];

  const ActionButtons = () => (
    <div className="flex items-center justify-end gap-2 p-3">
      <RequiredBuyPremiumButton>
        <Button
          variant="ghost"
          onClick={() => {
            onClean();
            onClose?.();
          }}
        >
          Limpar
        </Button>
      </RequiredBuyPremiumButton>
      <RequiredBuyPremiumButton>
        <Button
          onClick={() => {
            onFilter();
            onClose?.();
          }}
        >
          Filtrar
        </Button>
      </RequiredBuyPremiumButton>
    </div>
  );

  if (variant === "sheet") {
    return (
      <div className="w-full flex flex-1 flex-col gap-8 p-5 sm:p-7">
        <div className="flex items-center justify-end">
          <SheetClose asChild>
            <Button
              variant="outline"
              size="sm"
              aria-label="Fechar"
              className="p-2"
            >
              <XMarkIcon className="size-4" aria-hidden="true" />
            </Button>
          </SheetClose>
        </div>
        <Accordion type="single" collapsible className="w-full flex-1">
          {filterItems.map(({ id, icon: Icon, label, content }) => (
            <AccordionItem key={id} value={id}>
              <AccordionTrigger>
                <div className="flex gap-2 items-center">
                  <Icon className="size-4" aria-hidden="true" />
                  <span>{label}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="min-h-56">
                {content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <ActionButtons />
      </div>
    );
  }

  return (
    <>
      <NavigationMenu
        className="rounded-none"
        orientation="vertical"
        value={itemIndex}
        onValueChange={onItemIndexChange}
      >
        <NavigationMenuList>
          {filterItems.map(({ id, icon: Icon, label, content }) => (
            <NavigationMenuItem key={id} value={id} className="w-full">
              <NavigationMenuTrigger onClick={() => onItemIndexChange?.(id)}>
                <div className="flex gap-2 items-center">
                  <Icon className="size-4" aria-hidden="true" />
                  <span>{label}</span>
                </div>
              </NavigationMenuTrigger>
              <NavigationMenuContent>{content}</NavigationMenuContent>
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>
      <ActionButtons />
    </>
  );
};

interface Props {
  priceRange: PriceRange;
}

export const Filter = memo(({ priceRange }: Props) => {
  const {
    handler,
    state: {
      ordenar,
      tipo,
      modalidade,
      precoMin,
      precoMax,
      tamanhoArea,
      tamanhoPropriedade,
    },
  } = useActions();
  const { user } = useUser();
  const { hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);
  const [visible, setVisible] = useState(false);
  const [itemIndex, setItemIndex] = useState("1");

  const fetchFilter = () => {
    if (hasPaidPlan) {
      handler.fetchData(1);
    }
  };

  const cleanFilter = () => {
    if (hasPaidPlan) {
      handler.cleanData();
    }
  };

  const hasSortBy = ordenar !== defaultSortingCriteria;
  const hasModality = modalidade.length > 0;
  const hasType = tipo !== defaultTypeCriteria;
  const hasFilters = hasSortBy || hasModality || hasType;
  const hasSearchParams =
    hasFilters || precoMin || precoMax || tamanhoArea || tamanhoPropriedade;

  return (
    <Hidden
      content={[
        <Sheet key="mobile">
          <SheetTrigger asChild>
            <Button variant="outline" className="flex gap-2 w-full sm:w-auto">
              <FilterInfo />
            </Button>
          </SheetTrigger>
          <SheetContent
            aria-describedby={undefined}
            side="bottom"
            className="bg-white flex flex-col gap-10 overflow-auto rounded-tl-md rounded-tr-md w-full p-0 min-h-[70%] max-h-[70%]"
          >
            <VisuallyHidden asChild>
              <SheetTitle>Filtros</SheetTitle>
            </VisuallyHidden>
            <FilterContent
              priceRange={priceRange}
              onFilter={fetchFilter}
              onClean={cleanFilter}
              hideSearchInputs
              variant="sheet"
            />
          </SheetContent>
        </Sheet>,
        <div key="desktop" className="flex items-center gap-2 w-full sm:w-auto">
          <Popover open={visible} onOpenChange={setVisible}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex gap-2 w-full sm:w-auto">
                <FilterInfo />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[600px] p-0"
              side="bottom"
              align="start"
            >
              <FilterContent
                priceRange={priceRange}
                variant="popover"
                itemIndex={itemIndex}
                onItemIndexChange={(value) => {
                  if (value && value !== itemIndex) {
                    setItemIndex(value);
                  }
                }}
                onFilter={fetchFilter}
                onClean={cleanFilter}
                onClose={() => {
                  setVisible(false);
                }}
              />
            </PopoverContent>
          </Popover>
          {hasSearchParams ? (
            <Button
              variant="outline"
              size="sm"
              aria-label="Limpar filtros"
              className="p-2"
              onClick={cleanFilter}
            >
              <XMarkIcon className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </div>,
      ]}
    />
  );
});

Filter.displayName = "Filter";
